import { Sparkles } from "lucide-react";
import { useState } from "react";
import { Button } from "../../components/primitives";
import { AutoGrowTextArea } from "../editor/EditorControls";

/**
 * ASK ATLAS — a docked instruction composer, bound to the focused block.
 *
 * The loop the operator asked for: target a block → give an instruction → get a PREVIEWED proposed
 * edit into the same review flow (never applied blindly) → optionally research. The target chip is
 * shown only when a block is focused, because the backend now genuinely scopes the edit to that
 * block — the UI never promises targeting it cannot honor. Prose-only presets: no preset may imply a
 * layout, font, or callout change.
 */
const PRESETS = ["Match the funder's language", "Tighten to fit", "Lead with the audience's mission"];

export function AskAtlasComposer({
  targetLabel,
  busy,
  onSubmit
}: {
  targetLabel: string | null;
  busy: boolean;
  onSubmit: (prompt: string, research: boolean) => void;
}) {
  const [prompt, setPrompt] = useState("");
  const [research, setResearch] = useState(false);
  const canSend = Boolean(targetLabel) && prompt.trim().length > 2 && !busy;

  return (
    <section className="composer" data-testid="ask-atlas-composer">
      <div className="composer-head">
        <Sparkles aria-hidden="true" />
        <span className="composer-title">Ask ATLAS</span>
        {targetLabel ? (
          <span className="composer-target" data-testid="composer-target">Editing: {targetLabel}</span>
        ) : (
          <span className="composer-target composer-target-none">Click a paragraph to target it</span>
        )}
      </div>

      <div className="composer-presets">
        {PRESETS.map((preset) => (
          <button key={preset} type="button" className="composer-preset" onClick={() => setPrompt(preset)} disabled={busy}>
            {preset}
          </button>
        ))}
      </div>

      <AutoGrowTextArea
        className="composer-input"
        aria-label="Tell ATLAS what to change"
        data-testid="composer-input"
        placeholder="e.g. Restructure the opening around clinical safety and keep it tight."
        value={prompt}
        onValueChange={setPrompt}
        disabled={busy}
      />

      <div className="composer-actions">
        <label className="composer-research">
          <input type="checkbox" checked={research} onChange={(event) => setResearch(event.target.checked)} disabled={busy} />
          Research the funder
        </label>
        <Button
          variant="primary"
          size="sm"
          busy={busy}
          disabled={!canSend}
          data-testid="composer-submit"
          onClick={() => {
            onSubmit(prompt.trim(), research);
            setPrompt("");
          }}
        >
          {busy ? "Drafting…" : "Propose edit"}
        </Button>
      </div>
      <p className="composer-note">Returns a proposed edit you review — never applied directly.</p>
    </section>
  );
}
